import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgentChatListItem } from '@/api/client';
import { MessengerMutedStatus } from '@/components/Messenger/shared/chrome/MessengerMutedStatus';
import {
  orderAgentChatsForSidebar,
  readMessengerPinnedAgentChatIds,
  writeMessengerPinnedAgentChatIds,
} from '@/components/Messenger/shared/messengerPinnedAgentChats';
import { AgentChatSidebarRow } from '@/components/Messenger/sidebar/AgentChatSidebarRow';
import { AddIcon } from '@/components/ui/Icons';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import './AgentChatsSection.css';

export interface AgentChatsSectionProps {
  chats: AgentChatListItem[];
  /** Строка в анимации удаления — ещё в DOM, но сжимается. */
  exitingChatId?: string | null;
  processingByChatId: Record<string, boolean>;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  activeServerChatId: string | null;
  isDraftActive: boolean;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  onRenameChat: (chatId: string, defaultTitle: string) => void;
  onLoadMore: () => void;
  onOpen: () => void;
}

export function AgentChatsSection({
  chats,
  exitingChatId = null,
  processingByChatId,
  hasMore,
  loading,
  loadingMore,
  activeServerChatId,
  isDraftActive,
  onSelectChat,
  onDeleteChat,
  onRenameChat,
  onLoadMore,
  onOpen,
}: AgentChatsSectionProps) {
  const [openedOnce, setOpenedOnce] = useState(false);
  const [pinnedAgentChatIds, setPinnedAgentChatIds] = useState<string[]>([]);

  useEffect(() => {
    setPinnedAgentChatIds(readMessengerPinnedAgentChatIds());
  }, []);

  useEffect(() => {
    if (!openedOnce) {
      setOpenedOnce(true);
      onOpen();
    }
  }, [openedOnce, onOpen]);

  const orderedChats = useMemo(
    () => orderAgentChatsForSidebar(chats, pinnedAgentChatIds),
    [chats, pinnedAgentChatIds]
  );

  const pinnedAgentChatIdsSet = useMemo(
    () => new Set(pinnedAgentChatIds),
    [pinnedAgentChatIds]
  );

  const togglePinnedAgentChat = useCallback((chatId: string) => {
    setPinnedAgentChatIds((prev) => {
      const next = prev.includes(chatId)
        ? prev.filter((id) => id !== chatId)
        : [...prev, chatId];
      writeMessengerPinnedAgentChatIds(next);
      return next;
    });
  }, []);

  const handlePickChat = useCallback(
    (id: string) => {
      onSelectChat(id);
    },
    [onSelectChat]
  );

  const handleDeleteChat = useCallback(
    (id: string) => {
      setPinnedAgentChatIds((prev) => {
        if (!prev.includes(id)) return prev;
        const next = prev.filter((x) => x !== id);
        writeMessengerPinnedAgentChatIds(next);
        return next;
      });
      onDeleteChat(id);
    },
    [onDeleteChat]
  );

  return (
    <div className="agentChatsMenu">
      <OverlayScrollArea
        className="agentChatsMenu__scroll"
        defer={false}
        role="region"
        aria-label="Список чатов с Астрой"
      >
        <div className="agentChatsMenu__scrollBody">
          <div className="chatSidebar__itemStack">
          {loading && chats.length === 0 ? (
            <MessengerMutedStatus>Загрузка…</MessengerMutedStatus>
          ) : null}
          {!loading && chats.length === 0 ? (
            <MessengerMutedStatus>
              Пока нет сохранённых чатов. Отправьте сообщение Астре — чат появится здесь.
            </MessengerMutedStatus>
          ) : null}
          {orderedChats.map((c) => (
            <AgentChatSidebarRow
              key={c.id}
              chat={c}
              active={!isDraftActive && activeServerChatId === c.id}
              exiting={exitingChatId === c.id}
              unread={c.unread_count ?? 0}
              processing={Boolean(processingByChatId[c.id])}
              pinned={pinnedAgentChatIdsSet.has(c.id)}
              onPick={handlePickChat}
              onDelete={handleDeleteChat}
              onRenameChat={onRenameChat}
              onTogglePin={togglePinnedAgentChat}
            />
          ))}
        </div>
        {hasMore ? (
          <div className="chatSidebar__groupAddRow agentChatsMenu__loadMoreRow">
            <button
              type="button"
              className="chatSidebar__groupAddBtn chatSidebar__groupAddBtn_block"
              disabled={loadingMore}
              onClick={() => onLoadMore()}
            >
              <AddIcon size={18} className="chatSidebar__groupAddBtnPlus" aria-hidden />
              <span>{loadingMore ? 'Загрузка…' : 'Показать больше'}</span>
            </button>
          </div>
        ) : null}
        </div>
      </OverlayScrollArea>
    </div>
  );
}
